// Super-Admin Tenants management — full India-specific KYC support
import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { PageHeader } from '@/components/app/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { KYC_STATUS_LABEL, KYC_STATUS_COLOR } from '@/lib/constants';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { TenantKycDocs } from '@/components/app/TenantKycDocs';
import { NotesPanel } from '@/components/app/NotesPanel';

const EMPTY = {
  name: '', mobile: '', email: '',
  pan_number: '', aadhaar_last4: '', passport_number: '',
  govt_id_type: '', govt_id_number: '',
  permanent_address: '', current_address: '',
  occupation: '', company_name: '',
  emergency_contact_name: '', emergency_contact_mobile: '',
  reference_name: '', reference_mobile: '', reference_relation: '',
  police_verification_status: 'not_initiated',
  kyc_status: 'pending', kyc_remarks: '',
};

export default function AdminTenants() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(() => api.get('/tenants').then((r) => setItems(r.data)).catch(() => toast.error('Failed')), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!edit.name?.trim()) return toast.error('Name is required');
    try {
      const { id, _id, created_at, verified_by, verified_at, ...payload } = edit;
      if (id) await api.put(`/tenants/${id}`, payload);
      else await api.post('/tenants', payload);
      toast.success(id ? 'Updated' : 'Created');
      setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const remove = async () => {
    try {
      await api.delete(`/tenants/${confirmDelete.id}`);
      toast.success('Deleted'); setConfirmDelete(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Cannot delete'); }
  };

  return (
    <div>
      <PageHeader title="Tenants" subtitle="Full KYC, government IDs, and approval workflow" actions={
        <Button onClick={() => setEdit({ ...EMPTY })} data-testid="admin-tenants-add"><Plus className="h-4 w-4 mr-1" /> Add tenant</Button>
      } />

      <Card className="p-0 overflow-x-auto">
        <table className="w-full min-w-[820px] text-sm">
          <thead className="bg-muted/60">
            <tr>
              <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Name</th>
              <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Mobile</th>
              <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Email</th>
              <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">PAN</th>
              <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Aadhaar (last 4)</th>
              <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">KYC</th>
              <th className="px-3 py-2 text-xs uppercase text-muted-foreground text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!items.length && (
              <tr><td colSpan={7} className="py-10 text-center text-muted-foreground">No tenants yet.</td></tr>
            )}
            {items.map((t) => (
              <tr key={t.id} className="border-t hover:bg-muted/40" data-testid={`tenant-row-${t.id}`}>
                <td className="px-3 py-2 font-medium">{t.name}</td>
                <td className="px-3 py-2">{t.mobile || '—'}</td>
                <td className="px-3 py-2">{t.email || '—'}</td>
                <td className="px-3 py-2 font-mono text-xs">{t.pan_number || '—'}</td>
                <td className="px-3 py-2 font-mono text-xs">{t.aadhaar_last4 ? `XXXX-XXXX-${t.aadhaar_last4}` : '—'}</td>
                <td className="px-3 py-2">
                  <Badge variant="outline" className={KYC_STATUS_COLOR[t.kyc_status] || ''}>
                    <ShieldCheck className="h-3 w-3 mr-1" />{KYC_STATUS_LABEL[t.kyc_status] || t.kyc_status}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setEdit({ ...EMPTY, ...t })} data-testid={`tenant-edit-${t.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="h-8 px-2 text-destructive" onClick={() => setConfirmDelete(t)} data-testid={`tenant-delete-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="!max-w-2xl max-h-[92dvh] overflow-y-auto">
          <DialogHeader><DialogTitle>{edit?.id ? 'Edit tenant' : 'Add tenant'}</DialogTitle></DialogHeader>
          {edit && (
            <Tabs defaultValue="details" className="space-y-3">
              <TabsList>
                <TabsTrigger value="details" data-testid="tenant-tab-details">Details &amp; KYC</TabsTrigger>
                <TabsTrigger value="kyc-docs" data-testid="tenant-tab-kyc-docs" disabled={!edit.id}>KYC Documents</TabsTrigger>
                <TabsTrigger value="notes" data-testid="tenant-tab-notes" disabled={!edit.id}>Notes</TabsTrigger>
              </TabsList>
              <TabsContent value="details" className="space-y-4 mt-0">
              <Section title="Identity">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2"><Label>Full name <span className="text-destructive">*</span></Label><Input value={edit.name || ''} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="tenant-name" /></div>
                  <div><Label>Mobile</Label><Input value={edit.mobile || ''} onChange={(e) => setEdit({ ...edit, mobile: e.target.value })} placeholder="+91 ..." /></div>
                  <div><Label>Email</Label><Input type="email" value={edit.email || ''} onChange={(e) => setEdit({ ...edit, email: e.target.value })} /></div>
                  <div><Label>Occupation</Label><Input value={edit.occupation || ''} onChange={(e) => setEdit({ ...edit, occupation: e.target.value })} /></div>
                  <div><Label>Company / employer</Label><Input value={edit.company_name || ''} onChange={(e) => setEdit({ ...edit, company_name: e.target.value })} /></div>
                </div>
              </Section>

              <Section title="KYC Documents (India)">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label>PAN number</Label><Input value={edit.pan_number || ''} onChange={(e) => setEdit({ ...edit, pan_number: e.target.value.toUpperCase() })} maxLength={10} placeholder="ABCDE1234F" /></div>
                  <div><Label>Aadhaar (last 4 digits only)</Label><Input value={edit.aadhaar_last4 || ''} onChange={(e) => setEdit({ ...edit, aadhaar_last4: e.target.value.replace(/\D/g, '').slice(0, 4) })} maxLength={4} placeholder="1234" /></div>
                  <div><Label>Passport number</Label><Input value={edit.passport_number || ''} onChange={(e) => setEdit({ ...edit, passport_number: e.target.value.toUpperCase() })} /></div>
                  <div>
                    <Label>Other Govt ID type</Label>
                    <Select value={edit.govt_id_type || ''} onValueChange={(v) => setEdit({ ...edit, govt_id_type: v })}>
                      <SelectTrigger><SelectValue placeholder="(optional)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="voter_id">Voter ID</SelectItem>
                        <SelectItem value="driving_license">Driving License</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2"><Label>Other Govt ID number</Label><Input value={edit.govt_id_number || ''} onChange={(e) => setEdit({ ...edit, govt_id_number: e.target.value })} /></div>
                </div>
                <div className="text-[11px] text-muted-foreground mt-2">For privacy, only the last 4 digits of Aadhaar are stored. Full Aadhaar should be on a separate verified document.</div>
              </Section>

              <Section title="Address">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2"><Label>Permanent address</Label><Textarea rows={2} value={edit.permanent_address || ''} onChange={(e) => setEdit({ ...edit, permanent_address: e.target.value })} /></div>
                  <div className="sm:col-span-2"><Label>Current address</Label><Textarea rows={2} value={edit.current_address || ''} onChange={(e) => setEdit({ ...edit, current_address: e.target.value })} /></div>
                </div>
              </Section>

              <Section title="Emergency &amp; References">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label>Emergency contact name</Label><Input value={edit.emergency_contact_name || ''} onChange={(e) => setEdit({ ...edit, emergency_contact_name: e.target.value })} /></div>
                  <div><Label>Emergency contact mobile</Label><Input value={edit.emergency_contact_mobile || ''} onChange={(e) => setEdit({ ...edit, emergency_contact_mobile: e.target.value })} /></div>
                  <div><Label>Reference name</Label><Input value={edit.reference_name || ''} onChange={(e) => setEdit({ ...edit, reference_name: e.target.value })} /></div>
                  <div><Label>Reference mobile</Label><Input value={edit.reference_mobile || ''} onChange={(e) => setEdit({ ...edit, reference_mobile: e.target.value })} /></div>
                  <div><Label>Relation</Label><Input value={edit.reference_relation || ''} onChange={(e) => setEdit({ ...edit, reference_relation: e.target.value })} placeholder="Brother / Colleague" /></div>
                </div>
              </Section>

              <Section title="Verification">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Police verification</Label>
                    <Select value={edit.police_verification_status || 'not_initiated'} onValueChange={(v) => setEdit({ ...edit, police_verification_status: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="not_initiated">Not initiated</SelectItem>
                        <SelectItem value="initiated">Initiated</SelectItem>
                        <SelectItem value="cleared">Cleared</SelectItem>
                        <SelectItem value="failed">Failed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>KYC status</Label>
                    <Select value={edit.kyc_status || 'pending'} onValueChange={(v) => setEdit({ ...edit, kyc_status: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="submitted">Submitted (awaiting approval)</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2"><Label>KYC remarks</Label><Textarea rows={2} value={edit.kyc_remarks || ''} onChange={(e) => setEdit({ ...edit, kyc_remarks: e.target.value })} placeholder="Notes about KYC review" /></div>
                </div>
              </Section>
              </TabsContent>
              <TabsContent value="kyc-docs" className="mt-0">
                {edit.id ? <TenantKycDocs tenantId={edit.id} canManage={true} /> : <div className="text-xs text-muted-foreground py-4">Save the tenant first to upload KYC documents.</div>}
              </TabsContent>
              <TabsContent value="notes" className="mt-0">
                {edit.id ? <NotesPanel tenantId={edit.id} /> : <div className="text-xs text-muted-foreground py-4">Save the tenant first to add notes.</div>}
              </TabsContent>
            </Tabs>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setEdit(null)} className="w-full sm:w-auto">Cancel</Button>
            <Button onClick={save} className="w-full sm:w-auto" data-testid="tenant-save">{edit?.id ? 'Save changes' : 'Add tenant'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete tenant?</AlertDialogTitle>
            <AlertDialogDescription>This will remove <strong>{confirmDelete?.name}</strong>. Tenants with active leases cannot be deleted — terminate the lease first.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">{title}</div>
      {children}
    </div>
  );
}
